import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { euro } from '../lib/bookingChecks';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ClockCounterClockwise, PencilSimple, Trash, X as XIcon, Plus } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const ACTION_LABELS = {
  update: 'Bearbeitet',
  delete: 'Gelöscht',
  cancel: 'Storniert',
  create: 'Erstellt',
  status_change: 'Status geändert',
};

const ACTION_STYLES = {
  update: 'bg-blue-50 text-blue-700 border-blue-200',
  delete: 'bg-red-50 text-red-700 border-red-200',
  cancel: 'bg-orange-50 text-orange-700 border-orange-200',
  create: 'bg-green-50 text-green-700 border-green-200',
  status_change: 'bg-purple-50 text-purple-700 border-purple-200',
};

const ACTION_ICONS = {
  update: PencilSimple,
  delete: Trash,
  cancel: XIcon,
  create: Plus,
};

const FIELD_LABELS = {
  line_items: 'Positionen',
  discount: 'Rabatt',
  additional_charges: 'Zusatzgebühren',
  notes: 'Notizen',
  due_date: 'Fälligkeitsdatum',
  tax_rate: 'MwSt.',
  customer_id: 'Kunde',
  total: 'Gesamt',
  subtotal: 'Netto',
  tax_amount: 'MwSt.-Betrag',
  status: 'Status',
  paid_amount: 'Bezahlter Betrag',
  vehicle_id: 'Fahrzeug',
  start_date: 'Von',
  end_date: 'Bis',
  pickup_location: 'Abholort',
  return_location: 'Rückgabeort',
  pickup_mileage: 'Km-Stand',
  deposit_amount: 'Kaution',
  insurance_deductible: 'Selbstbehalt',
  use_weekend_rate: 'Wochenendtarif',
  extras: 'Extras',
  additional_drivers: 'Zusatzfahrer',
  customer_override: 'Abweichende Kundendaten',
  included_km: 'Inklusivkilometer',
  extra_km_rate: 'Preis je Mehrkilometer',
  price_info: 'Preisberechnung',
  valid_until: 'Gültig bis',
  storno_invoice_number: 'Storno-Nr.',
};

const DETAIL_LABELS = {
  first_name: 'Vorname', last_name: 'Nachname', date_of_birth: 'Geburtsdatum', address: 'Anschrift',
  driver_license_number: 'Führerscheinnummer', driver_license_expiry: 'Führerschein gültig bis',
  total_price: 'Gesamtpreis', base_price: 'Grundpreis', extras_price: 'Extras', daily_rate: 'Tagespreis',
  days: 'Miettage', manual_override: 'Manueller Preis', manual_price_note: 'Preisnotiz',
};
const formatValue = (v, field) => {
  if (field === 'additional_drivers' && Array.isArray(v)) return v.length ? v.map((driver, i) => `Fahrer ${i + 1}: ${Object.entries(driver).filter(([, value]) => value != null && value !== '').map(([key, value]) => `${DETAIL_LABELS[key] || key}: ${value}`).join(', ')}`).join(' · ') : 'Keine Zusatzfahrer';
  if (field === 'price_info' && v && typeof v === 'object') return Object.entries(v).filter(([key]) => key in DETAIL_LABELS).map(([key, value]) => `${DETAIL_LABELS[key]}: ${typeof value === 'boolean' ? (value ? 'ja' : 'nein') : ['total_price', 'base_price', 'extras_price', 'daily_rate'].includes(key) ? euro(value) : value}`).join(' · ');
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'ja' : 'nein';
  if (typeof v === 'object') {
    if (Array.isArray(v)) return `${v.length} Einträge`;
    return '[Objekt]';
  }
  const s = String(v);
  return s.length > 80 ? s.substring(0, 80) + '…' : s;
};

export const RevisionHistory = ({ open, onOpenChange, entityType, entityId, title }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (!entityType || !entityId) return;
    setLoading(true);
    setError(false);
    try {
      const res = await axios.get(`${API}/api/audit/${entityType}/${entityId}`, { withCredentials: true });
      setEntries(res.data || []);
    } catch (e) {
      setError(true);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClockCounterClockwise weight="bold" size={20} />
            Änderungsverlauf {title ? `· ${title}` : ''}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <p className="text-center text-[#8A94A6] py-6">Lade…</p>
        ) : error ? (
          <p role="alert" className="text-red-700 py-6">Der Änderungsverlauf konnte nicht geladen werden. Bitte erneut öffnen.</p>
        ) : entries.length === 0 ? (
          <p className="text-center text-[#8A94A6] py-6">Noch keine Änderungen protokolliert.</p>
        ) : (
          <div className="space-y-3" data-testid="revision-history-list">
            {entries.map((e) => {
              const Icon = ACTION_ICONS[e.action] || PencilSimple;
              return (
                <div key={e.id} className="border border-[#E2E4E9] rounded-sm p-3 bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded border ${ACTION_STYLES[e.action] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                      <Icon weight="bold" size={12} />
                      {ACTION_LABELS[e.action] || e.action}
                    </span>
                    <span className="text-xs text-[#8A94A6]">
                      {new Date(e.created_at).toLocaleString('de-DE')}
                    </span>
                  </div>
                  <p className="text-xs text-[#525866] mb-2">
                    <strong>{e.user_name || '—'}</strong>
                    {e.note ? <span> · {e.note}</span> : null}
                  </p>
                  {e.changes && Object.keys(e.changes).length > 0 && (
                    <div className="bg-[#F7F8FA] rounded-sm p-2 space-y-1">
                      {Object.entries(e.changes).map(([field, d]) => (
                        <div key={field} className="text-xs flex flex-wrap gap-x-2 break-words">
                          <span className="font-medium text-[#111418] min-w-[120px]">
                            {FIELD_LABELS[field] || field}
                          </span>
                          <span className="text-[#E60000] line-through">{formatValue(d.old, field)}</span>
                          <span className="text-[#8A94A6]">→</span>
                          <span className="text-[#009951]">{formatValue(d.new, field)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RevisionHistory;
